import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { ResponseError, instanceOfAxiosError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { HttpStatus } from '@utils/httpStatus'

import { clearLocalStorage } from '@hooks/useStorage'

import { Loading } from '@shared/components/Loading/Loading'
import {
  StatusTemplateBody,
  StatusTemplateWrapper,
} from '@shared/templates/StatusTemplate/StatusTemplate'

import { SignupError } from './SignupErrorPage'

export const ContinueRegistrationPage: React.FC = () => {
  const { t } = useTranslation()
  const { leadId } = useParams<{ leadId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const continueRegistration = useCallback(async () => {
    try {
      const marketingToken = searchParams.get('token')

      if (!leadId || !marketingToken) {
        setIsLoading(false)
        return
      }

      await apiClient.continueLeadRegistration({
        leadId,
        marketingToken: marketingToken,
      })
      setIsLoading(false)
    } catch (err: unknown) {
      if (instanceOfAxiosError((err as ResponseError)?.response)) {
        const errorResponse = (err as ResponseError).response.response?.data
        const errorCode = errorResponse?.code
        const status = (err as ResponseError).response.response?.status

        if (errorCode === SignupError.MarketingTokenExpired) {
          setHasError(true)
          clearLocalStorage()
          navigate('/login')
          return
        }

        if (status === HttpStatus.BAD_REQUEST) {
          setHasError(true)
        }
      }
    } finally {
      setIsLoading(false)
    }
  }, [apiClient, leadId, navigate, searchParams])

  useEffect(() => {
    continueRegistration()
  }, [continueRegistration])

  return (
    <Loading showLoadingIcon isLoading={isLoading}>
      <StatusTemplateWrapper>
        {hasError ? (
          <StatusTemplateBody title={t('Sign up.Something went wrong')} text={[]} icon='Fail' />
        ) : (
          <StatusTemplateBody
            title={t('Sign up.Check your email')}
            text={[t('Sign up.Security Reason')]}
            icon='Mail'
          />
        )}
      </StatusTemplateWrapper>
    </Loading>
  )
}
